import {
  convertFileListToFileUIParts,
  type FileUIPart,
  type TextUIPart,
} from "ai";
import {
  CHAT_ATTACHMENT_EXTENSION_MEDIA_TYPES,
  CHAT_ATTACHMENT_MAX_BYTES,
  CHAT_ATTACHMENT_MAX_COUNT,
  CHAT_ATTACHMENT_MEDIA_TYPES,
  CHAT_TEXT_ATTACHMENT_MAX_BYTES,
  CHAT_TEXT_BASED_ATTACHMENT_MEDIA_TYPES,
  type ChatAttachmentDataPart,
  createChatAttachmentDataPart,
  resolveChatAttachmentMediaType,
} from "@/lib/ai/chat-attachments";
import type { AnalysisTargetMentionInstance } from "./analysis-target-mention-token";

export const CHAT_SEND_ATTACHMENT_ACCEPT = [
  ...CHAT_ATTACHMENT_MEDIA_TYPES,
  ...Object.keys(CHAT_ATTACHMENT_EXTENSION_MEDIA_TYPES),
];
const CHAT_SEND_ATTACHMENT_MAX_BYTES = CHAT_ATTACHMENT_MAX_BYTES;
export const CHAT_SEND_ATTACHMENT_MAX_COUNT = CHAT_ATTACHMENT_MAX_COUNT;

export type ChatSendAttachment = {
  id: string;
  size: number;
  part: FileUIPart;
};

export type ChatSendFileValidationError =
  | {
      code: "max-count";
      maxCount: number;
    }
  | {
      code: "unsupported-type";
      filename: string;
    }
  | {
      code: "max-size";
      filename: string;
      maxMegabytes: number;
    }
  | {
      code: "invalid-text-encoding";
      filename: string;
    }
  | {
      code: "max-text-size";
      filename: string;
      maxKilobytes: number;
    };

/**
 * Prompt Input から送信時に渡す、本文と inline mention instance の束。
 *
 * `text` はユーザー可視の本文そのもので、`mentions` が本文中に埋め込まれた
 * target identity と textarea 範囲を保持する。
 */
export type ChatSendInput = {
  text: string;
  mentions: AnalysisTargetMentionInstance[];
  attachments?: ChatSendAttachment[];
};

export function validateChatSendFiles(files: File[]): {
  acceptedFiles: File[];
  errors: ChatSendFileValidationError[];
} {
  const acceptedFiles: File[] = [];
  const errors: ChatSendFileValidationError[] = [];
  const remainingSlots = CHAT_SEND_ATTACHMENT_MAX_COUNT;

  for (const file of files) {
    if (acceptedFiles.length >= remainingSlots) {
      errors.push({
        code: "max-count",
        maxCount: CHAT_SEND_ATTACHMENT_MAX_COUNT,
      });
      break;
    }

    const mediaType = resolveChatAttachmentMediaType(file);
    if (!CHAT_ATTACHMENT_MEDIA_TYPES.includes(mediaType as never)) {
      errors.push({ code: "unsupported-type", filename: file.name });
      continue;
    }

    const isTextAttachment = CHAT_TEXT_BASED_ATTACHMENT_MEDIA_TYPES.includes(
      mediaType as never,
    );
    if (isTextAttachment && file.size > CHAT_TEXT_ATTACHMENT_MAX_BYTES) {
      errors.push({
        code: "max-text-size",
        filename: file.name,
        maxKilobytes: CHAT_TEXT_ATTACHMENT_MAX_BYTES / 1024,
      });
      continue;
    }

    if (file.size > CHAT_SEND_ATTACHMENT_MAX_BYTES) {
      errors.push({
        code: "max-size",
        filename: file.name,
        maxMegabytes: CHAT_SEND_ATTACHMENT_MAX_BYTES / 1024 / 1024,
      });
      continue;
    }

    acceptedFiles.push(file);
  }

  return { acceptedFiles, errors };
}

/**
 * テキスト添付は UTF-8 として復号できるものだけを送信対象にする。
 *
 * モデル入力側では本文を UTF-8 として扱うため、置換文字を含む壊れた内容を
 * 送らないよう、添付時にユーザーへエラーとして返す。
 */
export async function validateChatSendTextFileEncodings(
  files: File[],
): Promise<{
  acceptedFiles: File[];
  errors: ChatSendFileValidationError[];
}> {
  const validations = await Promise.all(
    files.map(async (file) => {
      const mediaType = resolveChatAttachmentMediaType(file);
      if (
        !CHAT_TEXT_BASED_ATTACHMENT_MEDIA_TYPES.includes(mediaType as never)
      ) {
        return { file };
      }

      try {
        const bytes = await file.arrayBuffer();
        new TextDecoder("utf-8", { fatal: true }).decode(bytes);
        return { file };
      } catch {
        return {
          error: {
            code: "invalid-text-encoding" as const,
            filename: file.name,
          },
        };
      }
    }),
  );

  return {
    acceptedFiles: validations.flatMap((validation) =>
      validation.file ? [validation.file] : [],
    ),
    errors: validations.flatMap((validation) =>
      validation.error ? [validation.error] : [],
    ),
  };
}

function createFileList(files: File[]): FileList {
  const transfer = new DataTransfer();
  for (const file of files) {
    transfer.items.add(file);
  }
  return transfer.files;
}

async function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Failed to read file as data URL."));
    });
    reader.addEventListener("error", () => {
      reject(reader.error ?? new Error("Failed to read file."));
    });
    reader.readAsDataURL(file);
  });
}

async function convertFilesToFileUIParts(files: File[]): Promise<FileUIPart[]> {
  const fileParts =
    typeof DataTransfer !== "undefined" && typeof FileList !== "undefined"
      ? await convertFileListToFileUIParts(createFileList(files))
      : await Promise.all(
          files.map(async (file) => ({
            type: "file" as const,
            filename: file.name,
            mediaType: file.type,
            url: await readFileAsDataUrl(file),
          })),
        );

  return fileParts.map((part, index) => {
    const mediaType = resolveChatAttachmentMediaType(files[index]!);
    return {
      ...part,
      mediaType,
      url: replaceDataUrlMediaType(part.url, mediaType),
    };
  });
}

/** MIME 型を拡張子から補ったときも、data URL の宣言と一致させる。 */
function replaceDataUrlMediaType(url: string, mediaType: string): string {
  return url.replace(/^data:[^;,]*/, `data:${mediaType}`);
}

/**
 * サイズ・形式検査済みのファイルだけを AI SDK 公式 helper で `file` part に変換する。
 */
export async function convertAcceptedFilesToChatSendAttachments(
  files: File[],
  createId: () => string,
): Promise<ChatSendAttachment[]> {
  const fileParts = await convertFilesToFileUIParts(files);
  return fileParts.map((part, index) => ({
    id: createId(),
    size: files[index]?.size ?? 0,
    part,
  }));
}

export function buildChatMessageParts(params: {
  text: string;
  attachments?: ChatSendAttachment[];
}): Array<TextUIPart | FileUIPart | ChatAttachmentDataPart> {
  return [
    { type: "text", text: params.text },
    ...(params.attachments ?? []).map((attachment) => attachment.part),
    ...(params.attachments ?? []).map((attachment) =>
      createChatAttachmentDataPart({
        id: attachment.id,
        filename: attachment.part.filename,
        mediaType: attachment.part.mediaType,
        size: attachment.size,
      }),
    ),
  ];
}

/**
 * 送信候補テキストを検証し、送る本文を決める。
 *
 * 契約:
 * - 空送信判定だけ `trim()` を使う。
 * - 実際に返す本文は生文字列のまま保持し、mention offset を壊さない。
 *
 * 失敗時:
 * - 空白のみなら `null` を返す。
 */
export function resolveChatSendText(params: {
  submittedText?: string;
  fallbackText: string;
}): string | null {
  const text = params.submittedText ?? params.fallbackText;
  return text.trim().length > 0 ? text : null;
}
